import type { pisti } from "@app/rules";
import { useT } from "../i18n/context";

type Team = pisti.TeamDeckResult;

/** What each line of the count is worth to a team (Pagat's scoring, review 0029 S9). */
const ROWS = [
  { key: "pisti.count.cards", value: (team: Team) => String(team.cards) },
  { key: "pisti.count.aces", value: (team: Team) => String(team.aces) },
  { key: "pisti.count.jacks", value: (team: Team) => String(team.jacks) },
  { key: "pisti.count.clubTwo", value: (team: Team) => (team.clubTwo ? "2" : "0") },
  { key: "pisti.count.diamondTen", value: (team: Team) => (team.diamondTen ? "3" : "0") },
  { key: "pisti.count.majority", value: (team: Team) => (team.majority ? "3" : "0") },
  { key: "pisti.count.pistis", value: (team: Team) => String(team.pistiPoints) },
] as const;

/**
 * Between two decks the middle holds the count: what each team took and what it was worth (S15).
 * Everything in it is sized off one font size, so the table grows with the canvas instead of
 * sitting tiny in the middle of a wide one (owner, 20 Sept 2026).
 */
export function PistiCount({
  view,
  card,
}: {
  view: pisti.PistiView;
  card: { w: number; h: number };
}) {
  const { t } = useT();
  const result = view.lastHandResult;
  if (result === null) return null;
  const [even, odd] = result.teams;
  // Your team first, whichever side of the table it sits on.
  const teams = view.yourSeat % 2 === 0 ? [even, odd] : [odd, even];
  // The phone's canvas is the small one; there the table is already as big as its rect allows.
  const fontSize = Math.max(12, Math.round(13 * (card.w / 72)));
  return (
    <div role="status" className="flex h-full items-center justify-center">
      <table
        className="rounded-[0.8em] border border-edge bg-panel-strong leading-snug shadow-panel"
        style={{ fontSize }}
      >
        <caption className="sr-only">{t("pisti.count.title", { hand: result.hand })}</caption>
        <thead>
          <tr className="text-ink/70">
            <td className="px-[0.6em] pt-[0.45em]" />
            <th scope="col" className="px-[0.6em] pt-[0.45em] text-end font-bold">
              {t("pisti.count.us")}
            </th>
            <th scope="col" className="px-[0.6em] pt-[0.45em] text-end font-bold">
              {t("pisti.count.them")}
            </th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.key}>
              <th
                scope="row"
                className="whitespace-nowrap px-[0.6em] text-start font-semibold text-ink/80"
              >
                {t(row.key)}
              </th>
              {teams.map((team, i) => (
                <td key={i === 0 ? "us" : "them"} className="px-[0.6em] text-end tabular-nums">
                  {row.value(team)}
                </td>
              ))}
            </tr>
          ))}
          <tr className="font-extrabold text-gold">
            <th scope="row" className="px-[0.6em] pb-[0.45em] text-start">
              {t("pisti.count.total")}
            </th>
            {teams.map((team, i) => (
              <td
                key={i === 0 ? "us" : "them"}
                className="px-[0.6em] pb-[0.45em] text-end tabular-nums"
              >
                {team.delta}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
